import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Shield, 
  ArrowLeft,
  Lock,
  Eye,
  Server,
  Users,
  FileCheck,
  AlertTriangle,
  CheckCircle,
  Globe,
  Database,
  Key,
  RefreshCw,
  BookOpen,
  Mail,
  Phone
} from 'lucide-react';

const BilgiGuvenligi = () => {
  const navigate = useNavigate();

  const commitments = [
    {
      icon: Eye,
      title: 'Gizlilik',
      description: 'Tüm ilgili tarafların bilgi varlıklarına güvenli bir şekilde erişim sağlamayı ve bilginin gizliliğini korumayı'
    },
    {
      icon: Database,
      title: 'Bütünlük',
      description: 'Bilginin bütünlüğünü ve doğruluğunu her aşamada korumayı'
    },
    {
      icon: Server,
      title: 'Erişilebilirlik',
      description: 'Bilginin kullanılabilirliğini ve yetkili kullanıcıların erişimini kesintisiz sağlamayı'
    },
    {
      icon: FileCheck,
      title: 'Uyumluluk',
      description: 'Hizmet alan tüm müşterilerimize sözleşmelerde tanımlı gereksinimlere uyumun sağlanmasını'
    },
    {
      icon: Globe,
      title: 'Yasal Uyum',
      description: 'Çevreye saygılı, yasal gerekler ve mevzuatlar çerçevesinde faaliyetlerimizi sürdürmeyi'
    },
    {
      icon: AlertTriangle,
      title: 'Risk Yönetimi',
      description: 'Bilgi varlıkları üzerinde oluşabilecek riskleri değerlendirmeyi ve etkin şekilde yönetmeyi'
    }
  ];

  const policies = [
    'Kurumun güvenilirliğini ve marka imajını korumak',
    'Bilgi güvenliği politikalarını uygulamak, kontrolünü sağlamak ve olası ihlal durumlarında gerekli görülen yaptırımları uygulamak',
    'Tabi olduğu ulusal, uluslararası veya sektörel düzenlemelerden, ilgili mevzuat ve standart gereklerini yerine getirmek',
    'Anlaşmalardan doğan yükümlülüklerini karşılamak, iç ve dış paydaşlara yönelik kurumsal sorumlulukları sağlamak',
    'Bilgi güvenliği tehditlerinin etkisini azaltmak ve işin sürekliliği ile sürdürülebilirliğini sağlamak',
    'Bilginin işlendiği alanlarda bilginin güvenliğinin sağlanabilmesi amacıyla gerekli fiziksel ve çevresel güvenlik önlemlerini almak',
    'Yönetim sistemlerinin hedeflenen sonuçlara ulaşmasını sağlamak',
    'Kişisel veri güvenliği konusunda idari ve teknik tedbirleri almak',
    'Kurulan kontrol altyapısı ile bilgi güvenliği seviyesini korumak ve sürekli iyileştirmek',
    'Kurum içi farkındalığı arttırmak amacıyla yetkinlikleri geliştirecek eğitimleri sağlamak',
    'Gerçekleştirilen bilgi güvenliği çalışmalarının yasalara ve standartlara uygunluğunu tespit etmek için iç denetimler ve bağımsız 3. taraf kurumlar tarafından denetlenmek',
    'Politika kapsamındaki yükümlülüklerin çalışanlar ve ilgili taraflarca benimsenmesini sağlamak için gerekli iletişim ve rehberlik faaliyetlerini yürütmek',
    'Bu politikayı periyodik olarak gözden geçirerek güncelliğini ve etkinliğini sürdürmek'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-[#006D77] via-[#0A9396] to-[#006D77] overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-5 py-2.5 mb-6">
              <Shield className="w-5 h-5 text-white" />
              <span className="text-white font-semibold">BGYS Politikası</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Bilgi Güvenliği
              <span className="block text-[#F6AD55] mt-2">Yönetim Sistemi</span>
            </h1>
            
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Çerçeve Politikası
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="mb-8 border-[#006D77] text-[#006D77] hover:bg-[#006D77] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri Dön
          </Button>

          {/* Main Content Card */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            
            {/* Introduction */}
            <div className="p-8 border-b border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">BGYS Çerçeve Politikası</h2>
              </div>
              
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Şirketimiz, bilgi güvenliği yönetim sistemi kapsamında yürüttüğü faaliyetlerde 
                  <strong> bilgi varlıklarının gizliliğini, bütünlüğünü ve erişilebilirliğini</strong> korumayı 
                  temel ilke edinmiştir.
                </p>
                
                <div className="bg-[#006D77]/5 rounded-xl p-5 border border-[#006D77]/20">
                  <p>
                    Bu politika, şirketimizin bilgi güvenliği konusundaki yaklaşımını ve yönetim sistemlerine 
                    olan bağlılığını yansıtan bir genel beyandır. Yasal yükümlülüklerimiz ve kişisel verilere 
                    ilişkin aydınlatma yükümlülüklerimiz ayrıca ilgili dokümanlarda yerine getirilmektedir.
                  </p>
                </div>
              </div>
            </div>

            {/* Core Principles */}
            <div className="p-8 border-b border-gray-100 bg-gradient-to-r from-blue-50/50 to-transparent">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#006D77]" />
                Temel İlkelerimiz
              </h3>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {commitments.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-shadow">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-lg flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-bold text-gray-900 mb-2">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Commitments */}
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Taahhütlerimiz
              </h3>
              
              <p className="text-gray-600 mb-6">
                HizmetGeliyo olarak, Bilgi Güvenliği Yönetim Sistemi kapsamında aşağıdaki hususları taahhüt ederiz:
              </p>

              <div className="space-y-3">
                {policies.map((policy, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-gray-700 text-sm">{policy}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Security Measures */}
          <div className="mt-8 bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Key className="w-5 h-5 text-[#006D77]" />
                Güvenlik Önlemlerimiz
              </h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Server className="w-6 h-6 text-blue-600" />
                    <h4 className="font-bold text-gray-900">Teknik Güvenlik</h4>
                  </div>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>• SSL/TLS şifreleme</li>
                    <li>• Güvenlik duvarı koruması</li>
                    <li>• Düzenli güvenlik taramaları</li>
                    <li>• Veri yedekleme sistemleri</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Users className="w-6 h-6 text-green-600" />
                    <h4 className="font-bold text-gray-900">İdari Güvenlik</h4>
                  </div>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>• Erişim yetki kontrolü</li>
                    <li>• Personel güvenlik eğitimleri</li>
                    <li>• Gizlilik sözleşmeleri</li>
                    <li>• Düzenli iç denetimler</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 border border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Database className="w-6 h-6 text-purple-600" />
                    <h4 className="font-bold text-gray-900">Veri Güvenliği</h4>
                  </div>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>• Kişisel veri koruması (KVKK)</li>
                    <li>• Veri şifreleme</li>
                    <li>• Güvenli veri aktarımı</li>
                    <li>• Veri saklama politikaları</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <RefreshCw className="w-6 h-6 text-amber-600" />
                    <h4 className="font-bold text-gray-900">Sürekli İyileştirme</h4>
                  </div>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li>• Periyodik güvenlik değerlendirmeleri</li>
                    <li>• Bağımsız denetimler</li>
                    <li>• Politika güncellemeleri</li>
                    <li>• Tehdit izleme sistemleri</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Box */}
          <div className="mt-8 bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-3xl p-8 text-white">
            <div className="text-center mb-6">
              <Shield className="w-12 h-12 mx-auto mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-2">Güvenlik Sorularınız mı Var?</h3>
              <p className="text-white/80">
                Bilgi güvenliği konusundaki sorularınız için bizimle iletişime geçebilirsiniz.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="mailto:guvenlik@hizmetgeliyo.com"
                className="flex items-center justify-center gap-3 bg-white/20 hover:bg-white/30 rounded-xl p-4 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span className="font-semibold">guvenlik@hizmetgeliyo.com</span>
              </a>
              <a 
                href="tel:08502427808"
                className="flex items-center justify-center gap-3 bg-white/20 hover:bg-white/30 rounded-xl p-4 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span className="font-semibold">0850 242 78 08</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BilgiGuvenligi;
